import {Component, Input} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {MatDialogRef} from "@angular/material/dialog";
import {ToastrService} from "ngx-toastr";

interface FileUploadForm {
  fileDescription: FormControl<string>;
  fileCategory: FormControl<string>;
  fileName: FormControl<string>;
  fileContent: FormControl<string>;
  documentAction: FormControl<string>;
}

@Component({
  selector: 'app-file-manager-dialog-form',
  templateUrl: './file-manager-dialog-form.component.html',
  styleUrls: ['./file-manager-dialog-form.component.scss']
})
export class FileManagerDialogFormComponent {

  @Input() existingFileNames: string[] = [];
  @Input() currentTotalSize: number = 0;
  showActionChoice: boolean = false;

  readonly allowedExtensions: string[] = [
    'pdf', 'docx', 'xlsx', 'pptx', 'jpg', 'jpeg', 'png', 'tiff', 'tif', 'zip'
  ];

  readonly blockedExtensions: string[] = [
    'exe', 'bat', 'cmd', 'sh', 'vbs', 'js', 'scr', 'pif', 'com', 'jar', 'msi',
    'dll', 'ps1', 'apk', 'bin', 'app', 'wsf', 'cpl', 'gadget', 'vbe', 'jse',
    'reg', 'vb', 'hta', 'ins', 'isp', 'msc', 'msp', 'sct', 'wsh'
  ];

  readonly maxFileSize: number = 50 * 1024 * 1024; // 50 MB
  readonly maxTotalCaseSize: number = 500 * 1024 * 1024; // 500 MB

  constructor(
    private dialogRef: MatDialogRef<FileManagerDialogFormComponent>,
    private toastrService: ToastrService
  ) {}

  fileForm: FormGroup<FileUploadForm> = new FormGroup({
    fileDescription: new FormControl('', [Validators.required, Validators.minLength(5)]),
    fileCategory: new FormControl(''),
    fileName: new FormControl('', [Validators.required]),
    fileContent: new FormControl('', [Validators.required]),
    documentAction: new FormControl('replace', [Validators.required])
  });

  onFilechange(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    const fileName = file.name || '';
    const parts = fileName.split('.');
    const ext = parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';

    // 1. File name validation & Malware/Executable blocking
    const hasBlockedExt = this.blockedExtensions.includes(ext);
    const hasDoubleBlockedExt = parts.some((seg: string) => this.blockedExtensions.includes(seg.toLowerCase()));

    if (hasBlockedExt || hasDoubleBlockedExt) {
      this.toastrService.error('Executable files (.exe, scripts) and prohibited file types are strictly blocked.');
      event.target.value = '';
      this.fileForm.patchValue({ fileName: '', fileContent: '' });
      return;
    }

    // 2. Allowed file types: PDF, DOCX, XLSX, PPTX, JPG, PNG, TIFF, ZIP
    if (!this.allowedExtensions.includes(ext)) {
      this.toastrService.error(`File type '.${ext}' is not permitted. Allowed types: PDF, DOCX, XLSX, PPTX, JPG, PNG, TIFF, ZIP.`);
      event.target.value = '';
      this.fileForm.patchValue({ fileName: '', fileContent: '' });
      return;
    }

    // 3. Maximum file size per attachment: 50 MB
    if (file.size > this.maxFileSize) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      this.toastrService.error(`File size (${sizeMb} MB) exceeds the maximum allowed limit of 50 MB.`);
      event.target.value = '';
      this.fileForm.patchValue({ fileName: '', fileContent: '' });
      return;
    }

    // 4. Maximum total attachment size per case: 500 MB
    if ((this.currentTotalSize + file.size) > this.maxTotalCaseSize) {
      this.toastrService.error('Total attachments for this case exceed the 500 MB limit.');
      event.target.value = '';
      this.fileForm.patchValue({ fileName: '', fileContent: '' });
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      let base64String = '';
      if (typeof reader.result === "string") {
        base64String = reader.result
          .replace('data:', '')
          .replace(/^.+,/, '');
      }

      this.fileForm.patchValue({ fileName: file.name, fileContent: base64String });

      if (this.existingFileNames && this.existingFileNames.length > 0) {
        const isDuplicate = this.existingFileNames.some(
          name => name?.toLowerCase().trim() === file.name?.toLowerCase().trim()
        );
        this.showActionChoice = isDuplicate;
      }
    };

    reader.readAsDataURL(file);
  }

  submit(isValid: boolean): void {
    if (isValid) {
      const formValue = this.fileForm.value;
      this.dialogRef.close({
        ...formValue,
        documentAction: this.showActionChoice ? formValue.documentAction : 'new'
      });
    } else {
      this.toastrService.error("Please fill in required fields*");
    }
  }
}
